import { useState } from 'react';
import { Link } from 'react-router-dom';
import type { Account, Card, Transaction } from '@shared/contracts/entities';
import { Amount, ListRow, SectionHeader, StatusBadge, TransactionMark } from '@/components/banking';
import { CardSkeleton, EmptyState, ErrorState, LoadingRegion, RowSkeleton } from '@/components/feedback';
import { Sheet } from '@/components/feedback/Sheet';
import { formatDayGroup, formatPercentageChange, formatTime } from '@/services/formatters';
import type { ApprovalListItem, FinanceSummary } from '@/mocks/api';
import type { UseAsyncResult } from '@/hooks/useAsync';
import type { MobileWidgetId } from '@/customisation/widgetRegistry';

/**
 * Mobile home widgets.
 *
 * Each widget owns its own loading, empty and error states so one slow or
 * failed region never blanks the whole page.
 */

export type HomeData = {
  readonly accounts: UseAsyncResult<Account[]>;
  readonly transactions: UseAsyncResult<Transaction[]>;
  readonly approvals: UseAsyncResult<ApprovalListItem[]>;
  readonly cards: UseAsyncResult<Card[]>;
  readonly summary: UseAsyncResult<FinanceSummary>;
  readonly baseCurrency: string;
  readonly canCreatePayment: boolean;
  readonly canExpense: boolean;
  readonly canFx: boolean;
};

/* --------------------------------------------------------- total balance -- */

/**
 * TRACE:TT-R111-01 — a total-balance module exists on /mobile/home.
 * TRACE:TT-R111-02 — tapping it routes to the accounts management page.
 *
 * TRACE:TT-R102-02 — this module carries NO transfer action. Money movement
 * lives solely in Quick actions, so the two are never duplicated.
 */
export function TotalBalanceWidget({ data }: { data: HomeData }) {
  const { accounts, baseCurrency } = data;

  if (accounts.status === 'loading') {
    return (
      <LoadingRegion label="Loading your total balance">
        <CardSkeleton height={132} />
      </LoadingRegion>
    );
  }
  if (accounts.status === 'error') {
    return (
      <ErrorState
        error={accounts.error}
        onRetry={accounts.reload}
        title="We could not load your balance"
      />
    );
  }

  const inBase = accounts.data.filter((a) => a.currency === baseCurrency);
  const totalAvailable = inBase.reduce((sum, a) => sum + a.availableBalance, 0);
  const totalBooked = inBase.reduce((sum, a) => sum + a.balance, 0);
  const otherCurrencies = [...new Set(accounts.data.map((a) => a.currency))].filter(
    (c) => c !== baseCurrency,
  );

  return (
    <Link
      to="/mobile/accounts"
      className="tm-card tm-card--accent"
      style={{ textDecoration: 'none', display: 'block' }}
      aria-label={`Total available balance. View all accounts.`}
    >
      <p className="tm-eyebrow">Total available</p>
      <Amount minorUnits={totalAvailable} currency={baseCurrency} size="hero" />

      {/* Available and booked are shown separately and never conflated. */}
      {totalBooked !== totalAvailable ? (
        <p className="tm-muted" style={{ margin: '6px 0 0' }}>
          <Amount minorUnits={totalBooked} currency={baseCurrency} /> booked ·{' '}
          <Amount minorUnits={totalBooked - totalAvailable} currency={baseCurrency} /> reserved
        </p>
      ) : null}

      <p className="tm-muted" style={{ margin: '10px 0 0' }}>
        {inBase.length} {baseCurrency} account{inBase.length === 1 ? '' : 's'}
        {otherCurrencies.length > 0 ? ` · also ${otherCurrencies.join(', ')}` : ''} · View all
      </p>
    </Link>
  );
}

/* ---------------------------------------------------------- quick actions -- */

type QuickAction = { id: string; label: string; to: string; description: string };

/**
 * TRACE:TT-R100-01 — a Quick actions block exists on /mobile/home.
 * TRACE:TT-R099-02 — the trigger is a purpose-clear labelled control, never a
 * bare "+", so its purpose is expectable without guessing.
 * TRACE:TT-R099-03 — activating it reveals multiple named action buttons.
 * TRACE:TT-R113-02 — the claim action is labelled "New expense claim" and opens
 * the expense-claim flow (it is not called "Pay bills").
 */
export function QuickActionsWidget({ data }: { data: HomeData }) {
  const [open, setOpen] = useState(false);

  const actions: QuickAction[] = [];
  if (data.canCreatePayment) {
    actions.push({
      id: 'send',
      label: 'Send money',
      to: '/mobile/payments/new',
      description: 'Pay a beneficiary from one of your accounts',
    });
    actions.push({
      id: 'transfer',
      label: 'Move between accounts',
      to: '/mobile/payments/new?mode=transfer',
      description: 'Transfer between your own accounts',
    });
  }
  actions.push({
    id: 'request',
    label: 'Get paid',
    to: '/mobile/accounts',
    description: 'Share your account details to receive money',
  });
  if (data.canFx) {
    actions.push({
      id: 'exchange',
      label: 'Exchange currency',
      to: '/mobile/fx',
      description: 'Convert between your currencies',
    });
  }
  if (data.canExpense) {
    actions.push({
      id: 'expense',
      label: 'New expense claim',
      to: '/mobile/expenses',
      description: 'Submit a receipt for reimbursement',
    });
  }

  const primary = actions.slice(0, 3);

  return (
    <section aria-labelledby="home-quick-actions">
      <SectionHeader title="Quick actions" id="home-quick-actions" />
      <div className="tm-quick-actions">
        {primary.map((action) => (
          <Link key={action.id} to={action.to} className="tm-quick-action">
            <span className="tm-quick-action__label">{action.label}</span>
          </Link>
        ))}
        {actions.length > primary.length ? (
          <button
            type="button"
            className="tm-quick-action tm-quick-action--more"
            onClick={() => setOpen(true)}
            aria-haspopup="dialog"
          >
            <span className="tm-quick-action__label">All actions</span>
          </button>
        ) : null}
      </div>

      <Sheet open={open} onClose={() => setOpen(false)} title="All quick actions">
        <ul className="tm-rows">
          {actions.map((action) => (
            <li key={action.id}>
              <ListRow
                to={action.to}
                title={action.label}
                meta={action.description}
                ariaLabel={`${action.label}. ${action.description}`}
              />
            </li>
          ))}
        </ul>
      </Sheet>
    </section>
  );
}

/* -------------------------------------------------------- needs attention -- */

/**
 * TRACE:TT-R101-01 — a "Needs attention" section exists on /mobile/home.
 * TRACE:TT-R105-01 — approvals surface here rather than in a second,
 * redundant "Approvals" section alongside this one.
 * TRACE:TT-R105-02 — a pending approval therefore appears in exactly one place
 * on home.
 */
export function NeedsAttentionWidget({ data }: { data: HomeData }) {
  const { approvals, transactions, cards } = data;

  const items: { id: string; title: string; meta: string; to: string; tone: 'warning' | 'danger' }[] =
    [];

  for (const { approval, payment } of approvals.data ?? []) {
    items.push({
      id: approval.id,
      title: 'Payment needs your approval',
      meta: `${payment.reference} · awaiting a decision`,
      to: `/mobile/approvals/${approval.id}`,
      tone: 'warning',
    });
  }
  for (const tx of (transactions.data ?? []).filter((t) => t.status === 'failed')) {
    items.push({
      id: tx.id,
      title: 'Payment failed',
      meta: `${tx.counterparty} · no money left your account`,
      to: `/mobile/transactions/${tx.id}`,
      tone: 'danger',
    });
  }
  for (const card of (cards.data ?? []).filter(
    (c) => c.lifecycleStatus === 'blocked' && c.statusReason?.includes('bank'),
  )) {
    items.push({
      id: card.id,
      title: 'Card blocked by your bank',
      meta: `Card ending ${card.last4}`,
      to: `/mobile/cards/${card.id}`,
      tone: 'danger',
    });
  }

  const loading = approvals.status === 'loading' || transactions.status === 'loading';

  return (
    <section aria-labelledby="home-attention">
      <SectionHeader title="Needs attention" id="home-attention" />
      {loading ? (
        <LoadingRegion label="Checking what needs attention">
          <div className="tm-card tm-card--flush">
            <RowSkeleton count={2} />
          </div>
        </LoadingRegion>
      ) : items.length === 0 ? (
        <div className="tm-card">
          <p className="tm-muted" style={{ margin: 0 }}>
            Nothing needs your attention right now.
          </p>
        </div>
      ) : (
        <div className="tm-card tm-card--flush">
          <ul className="tm-rows">
            {items.map((item) => (
              <li key={item.id}>
                <ListRow
                  to={item.to}
                  title={item.title}
                  meta={item.meta}
                  end={
                    <StatusBadge
                      status={item.tone === 'danger' ? 'failed' : 'pending'}
                      label={item.tone === 'danger' ? 'Action needed' : 'Awaiting you'}
                      tone={item.tone}
                    />
                  }
                  ariaLabel={`${item.title}. ${item.meta}`}
                />
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}

/* -------------------------------------------------------------- money flow -- */

/**
 * TRACE:TT-R122-01 — home shows money-direction metrics.
 * TRACE:TT-R122-02 — it uses the canonical cross-surface terms "Money in" and
 * "Money out". The vocabulary contract forbids mobile saying
 * "Income/Expenses" while web says "Inflow/Outflow".
 * TRACE:TT-R103-01 — percentage-change indicators exist.
 * TRACE:TT-R103-02 — each states its comparison period.
 */
export function MoneyFlowWidget({ data }: { data: HomeData }) {
  const { summary } = data;

  if (summary.status === 'loading') {
    return (
      <LoadingRegion label="Loading money in and out">
        <CardSkeleton height={116} />
      </LoadingRegion>
    );
  }
  if (summary.status === 'error') {
    return <ErrorState error={summary.error} onRetry={summary.reload} title="We could not load your totals" />;
  }

  const s = summary.data;
  const inChange = formatPercentageChange(s.moneyInChangeRatio, s.comparisonLabel);
  const outChange = formatPercentageChange(s.moneyOutChangeRatio, s.comparisonLabel);

  return (
    <section aria-labelledby="home-flow">
      <SectionHeader
        title="Money in and out"
        id="home-flow"
        action={<span className="tm-hint">{s.periodLabel}</span>}
      />
      <div className="tm-card">
        <div className="tm-flow-grid">
          <div>
            <p className="tm-eyebrow">Money in</p>
            <Amount minorUnits={s.moneyInMinor} currency={s.currency} size="lg" direction="in" />
            <p className="tm-hint" style={{ margin: '4px 0 0' }}>
              <span aria-hidden="true">
                {inChange.value} {inChange.period}
              </span>
              <span className="tm-visually-hidden">{inChange.accessibleLabel}</span>
            </p>
          </div>
          <div>
            <p className="tm-eyebrow">Money out</p>
            <Amount minorUnits={s.moneyOutMinor} currency={s.currency} size="lg" />
            <p className="tm-hint" style={{ margin: '4px 0 0' }}>
              <span aria-hidden="true">
                {outChange.value} {outChange.period}
              </span>
              <span className="tm-visually-hidden">{outChange.accessibleLabel}</span>
            </p>
          </div>
        </div>
        <hr className="tm-divider" style={{ margin: '12px 0' }} />
        <Link to="/mobile/reports" className="tm-btn tm-btn--quiet tm-btn--sm">
          See full report
        </Link>
      </div>
    </section>
  );
}

/* ---------------------------------------------------------- recent activity */

/**
 * TRACE:TT-R114-01 — a movement/activity list exists on /mobile/home.
 * TRACE:TT-R114-02 — tapping a movement row opens the detailed transaction view.
 * TRACE:TT-R126-01 — a "See all activity" destination exists off home.
 */
export function RecentActivityWidget({ data }: { data: HomeData }) {
  const { transactions } = data;

  return (
    <section aria-labelledby="home-activity">
      <SectionHeader
        title="Recent activity"
        id="home-activity"
        action={
          <Link to="/mobile/transactions" className="tm-btn tm-btn--quiet tm-btn--sm">
            See all activity
          </Link>
        }
      />
      {transactions.status === 'loading' ? (
        <LoadingRegion label="Loading recent activity">
          <div className="tm-card tm-card--flush">
            <RowSkeleton count={4} />
          </div>
        </LoadingRegion>
      ) : transactions.status === 'error' ? (
        <ErrorState error={transactions.error} onRetry={transactions.reload} />
      ) : transactions.data.length === 0 ? (
        <div className="tm-card">
          <EmptyState
            title="No activity yet"
            body="Once money moves in or out, you will see it here."
          />
        </div>
      ) : (
        <div className="tm-card tm-card--flush">
          <ul className="tm-rows">
            {transactions.data.slice(0, 5).map((tx) => (
              <li key={tx.id}>
                <ListRow
                  to={`/mobile/transactions/${tx.id}`}
                  leading={<TransactionMark direction={tx.direction} />}
                  title={tx.counterparty}
                  meta={`${formatDayGroup(tx.occurredAt)} at ${formatTime(tx.occurredAt)}${tx.reference ? ` · ${tx.reference}` : ''}`}
                  end={
                    <span style={{ display: 'grid', gap: 4, justifyItems: 'end' }}>
                      <Amount
                        minorUnits={tx.amount}
                        currency={tx.currency}
                        signed
                        direction={tx.direction}
                      />
                      {tx.status !== 'completed' ? <StatusBadge status={tx.status} /> : null}
                    </span>
                  }
                  ariaLabel={`${tx.counterparty}, ${tx.direction === 'in' ? 'received' : 'sent'}, ${tx.status}`}
                />
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}

/* ------------------------------------------------------------ cards summary */

/**
 * TRACE:TT-R112-01 — a cards entry point exists on home, outside the
 * quick-actions row.
 * TRACE:TT-R112-02 — it routes to the cards area at /mobile/cards.
 * TRACE:TT-R130-01 — home presents both the accounts/IBAN concept and the
 * cards concept, so either could be foregrounded for a tenant.
 */
export function CardsSummaryWidget({ data }: { data: HomeData }) {
  const { cards } = data;

  return (
    <section aria-labelledby="home-cards">
      <SectionHeader
        title="Cards"
        id="home-cards"
        action={
          <Link to="/mobile/cards" className="tm-btn tm-btn--quiet tm-btn--sm">
            All cards
          </Link>
        }
      />
      {cards.status === 'loading' ? (
        <LoadingRegion label="Loading your cards">
          <div className="tm-card tm-card--flush">
            <RowSkeleton count={2} />
          </div>
        </LoadingRegion>
      ) : cards.status === 'error' ? (
        <ErrorState error={cards.error} onRetry={cards.reload} />
      ) : cards.data.length === 0 ? (
        <div className="tm-card">
          <p className="tm-muted" style={{ margin: 0 }}>
            You do not have a card yet.
          </p>
        </div>
      ) : (
        <div className="tm-card tm-card--flush">
          <ul className="tm-rows">
            {cards.data.slice(0, 3).map((card) => (
              <li key={card.id}>
                <ListRow
                  to={`/mobile/cards/${card.id}`}
                  leading={<CardMark type={card.cardType} />}
                  title={`•••• ${card.last4}`}
                  meta={`${card.cardholderName} · ${card.cardType === 'virtual' ? 'Virtual' : 'Physical'}`}
                  end={<StatusBadge status={card.lifecycleStatus} />}
                  ariaLabel={`Card ending ${card.last4}, ${card.cardholderName}, ${card.lifecycleStatus}`}
                />
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}

/**
 * Card scheme / product mark.
 *
 * TRACE:TT-R110-02 — account rows that have an associated card display a
 * card mark, so the account/card relationship is visible at a glance.
 */
export function CardMark({ type }: { type: 'physical' | 'virtual' }) {
  return (
    <span className="tm-card-mark" aria-hidden="true" data-card-type={type}>
      <svg width="24" height="16" viewBox="0 0 24 16" fill="none" aria-hidden="true">
        <rect x="0.5" y="0.5" width="23" height="15" rx="2.5" stroke="currentColor" />
        <rect x="0.5" y="4" width="23" height="2.5" fill="currentColor" opacity="0.45" />
        {type === 'virtual' ? (
          <circle cx="18" cy="11" r="2" fill="currentColor" opacity="0.5" />
        ) : (
          <rect x="3" y="9.5" width="6" height="3" rx="0.8" fill="currentColor" opacity="0.5" />
        )}
      </svg>
    </span>
  );
}

/* -------------------------------------------------------- finance shortcuts */

/**
 * TRACE:TT-R133-01 — finance widgets (Treasury, Invoices, Tax calendar) exist
 * and open from the Finance section.
 * TRACE:TT-R106-01 — FX market rates live on the exchange/FX page, not on home;
 * TRACE:TT-R106-02 — home provides a link to FX instead of embedding raw rates.
 */
export function FinanceShortcutsWidget({ data }: { data: HomeData }) {
  const items = [
    { id: 'treasury', label: 'Treasury', meta: 'Balances and runway', to: '/mobile/reports' },
    { id: 'invoices', label: 'Invoices', meta: 'What you are owed', to: '/mobile/invoices' },
    { id: 'tax', label: 'Tax calendar', meta: 'Upcoming obligations', to: '/mobile/tax' },
    ...(data.canFx
      ? [{ id: 'fx', label: 'Exchange rates', meta: 'Live rates and conversion', to: '/mobile/fx' }]
      : []),
  ];

  return (
    <section aria-labelledby="home-finance">
      <SectionHeader title="Finance" id="home-finance" />
      <div className="tm-card tm-card--flush">
        <ul className="tm-rows">
          {items.map((item) => (
            <li key={item.id}>
              <ListRow to={item.to} title={item.label} meta={item.meta} />
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------ accounts list */

/**
 * TRACE:TT-R110-01 — a bottom accounts section exists on /mobile/home.
 * TRACE:TT-R110-02 — an account that has an associated card shows a card mark.
 */
export function AccountsListWidget({ data }: { data: HomeData }) {
  const { accounts, cards } = data;
  const accountsWithCards = new Set((cards.data ?? []).map((c) => c.accountId));

  return (
    <section aria-labelledby="home-accounts">
      <SectionHeader
        title="Accounts"
        id="home-accounts"
        action={
          <Link to="/mobile/accounts" className="tm-btn tm-btn--quiet tm-btn--sm">
            Manage
          </Link>
        }
      />
      {accounts.status === 'loading' ? (
        <LoadingRegion label="Loading your accounts">
          <div className="tm-card tm-card--flush">
            <RowSkeleton count={3} />
          </div>
        </LoadingRegion>
      ) : accounts.status === 'error' ? (
        <ErrorState error={accounts.error} onRetry={accounts.reload} />
      ) : accounts.data.length === 0 ? (
        <div className="tm-card">
          <EmptyState title="No accounts yet" body="Your accounts will appear here once opened." />
        </div>
      ) : (
        <div className="tm-card tm-card--flush">
          <ul className="tm-rows">
            {accounts.data.map((account) => (
              <li key={account.id}>
                <ListRow
                  to={`/mobile/accounts/${account.id}`}
                  {...(accountsWithCards.has(account.id)
                    ? { leading: <CardMark type="physical" /> }
                    : {})}
                  title={account.displayName}
                  meta={`${account.currency}${account.localDetails?.accountNumber ? ` · ••${account.localDetails.accountNumber.slice(-4)}` : ''}`}
                  end={
                    <span style={{ display: 'grid', gap: 4, justifyItems: 'end' }}>
                      <Amount minorUnits={account.availableBalance} currency={account.currency} />
                      {account.status !== 'active' ? <StatusBadge status={account.status} /> : null}
                    </span>
                  }
                  ariaLabel={`${account.displayName}, ${account.currency}, status ${account.status}`}
                />
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}

/* ------------------------------------------------------------------ mapping */

export const WIDGET_COMPONENTS: Readonly<
  Record<MobileWidgetId, (props: { data: HomeData }) => JSX.Element>
> = {
  'total-balance': TotalBalanceWidget,
  'quick-actions': QuickActionsWidget,
  'needs-attention': NeedsAttentionWidget,
  'money-flow': MoneyFlowWidget,
  'recent-activity': RecentActivityWidget,
  'cards-summary': CardsSummaryWidget,
  'finance-shortcuts': FinanceShortcutsWidget,
  'accounts-list': AccountsListWidget,
};
